import { useState } from "react";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import { formService } from "../service";

// All supported backend field types
export const FIELD_OPTIONS = [
  { label: "Identifiant unique", value: "id" },
  { label: "Texte court (string)", value: "string" },
  { label: "Texte long (textarea)", value: "text" },
  { label: "Nombre entier", value: "integer" },
  { label: "Nombre décimal", value: "float" },
  { label: "Booléen (Oui/Non)", value: "boolean" },
  { label: "Titre de section", value: "section-title" },
  { label: "Choix multiple", value: "checkbox" },
  { label: "Choix unique", value: "radio" },
  { label: "Date", value: "date" },
];

export const useNewForm = (studyId, onBack) => {
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState([
    {
      id: Date.now(),
      label: "",
      field_type: "",
      required: false,
      conditions: {},
    },
  ]);

  // ---------------------------
  //  CRUD helpers for fields
  // ---------------------------

  const addField = () =>
    setFields((prev) => [
      ...prev,
      {
        id: Date.now(),
        label: "",
        field_type: "",
        required: false,
        conditions: {},
      },
    ]);

  const deleteField = (id) =>
    setFields((prev) => prev.filter((f) => f.id !== id));

  const updateField = (id, key, value) =>
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: value } : f))
    );

  // ---------------------------
  // Option management
  // ---------------------------

  const addOption = (fieldId, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              conditions: {
                ...f.conditions,
                options: [...(f.conditions.options || []), value],
              },
            }
          : f
      )
    );
  };

  const removeOption = (fieldId, value) => {
    setFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              conditions: {
                ...f.conditions,
                options: (f.conditions.options || []).filter(
                  (opt) => opt !== value
                ),
              },
            }
          : f
      )
    );
  };

  // ---------------------------
  //  Render extra field editors (used by NewForm)
  // ---------------------------

  const renderExtraFields = (field, updateField) => {
    switch (field.field_type) {
      case "id":
        return {
          type: "radio",
          label: "Générer automatiquement l’identifiant",
          options: [
            { label: "Oui", value: true },
            { label: "Non", value: false },
          ],
          name: `auto-${field.id}`,
          selected: field.conditions?.auto_generated ?? true,
          onChange: (val) =>
            updateField(field.id, "conditions", {
              ...field.conditions,
              auto_generated: val,
            }),
        };

      case "integer":
      case "float":
        return {
          type: "number-range",
          label: "Valeur minimale et maximale",
          min: field.conditions?.min || "",
          max: field.conditions?.max || "",
          onChangeMin: (val) =>
            updateField(field.id, "conditions", {
              ...field.conditions,
              min: val ? Number(val) : "",
            }),
          onChangeMax: (val) =>
            updateField(field.id, "conditions", {
              ...field.conditions,
              max: val ? Number(val) : "",
            }),
        };

      case "date":
        return {
          type: "date-range",
          label: "Date minimale et maximale",
          min: field.conditions?.min || "",
          max: field.conditions?.max || "",
          onChangeMin: (val) =>
            updateField(field.id, "conditions", {
              ...field.conditions,
              min: val,
            }),
          onChangeMax: (val) =>
            updateField(field.id, "conditions", {
              ...field.conditions,
              max: val,
            }),
        };

      case "checkbox":
      case "radio":
        return {
          type: "options",
          label:
            field.field_type === "checkbox"
              ? "Choix multiples"
              : "Choix uniques",
          options: field.conditions?.options || [],
          onAdd: (val) => addOption(field.id, val),
          onRemove: (val) => removeOption(field.id, val),
        };

      default:
        return null;
    }
  };

  // ---------------------------
  //  Normalize field before sending
  // ---------------------------

  const normalizeField = (field) => {
    const base = {
      label: field.label.trim(),
      field_type: field.field_type,
      required: field.required,
      conditions: {},
    };

    switch (field.field_type) {
      case "id":
        base.conditions = {
          auto_generated: field.conditions?.auto_generated ?? true,
        };
        break;
      case "integer":
      case "float":
      case "date":
        base.conditions = {
          min: field.conditions?.min || null,
          max: field.conditions?.max || null,
        };
        break;
      case "checkbox":
      case "radio":
        base.conditions = {
          options: field.conditions?.options?.filter((o) => o.trim()) || [],
        };
        break;
      default:
        base.conditions = field.conditions || {};
    }

    return base;
  };

  // ---------------------------
  // Save form to backend
  // ---------------------------

  const handleSave = async () => {
    try {
      if (!formTitle.trim()) {
        showErrorToast("Le titre du formulaire est obligatoire.");
        return;
      }

      const normalizedStudyId =
        typeof studyId === "object" ? studyId.id : studyId;

      // Filter out empty or incomplete fields
      const validFields = fields
        .map(normalizeField)
        .filter(
          (f) =>
            f.label.trim() !== "" && f.field_type && f.field_type.trim() !== ""
        );

      const payload = {
        title: formTitle.trim(),
        description: formDescription.trim(),
        fields: validFields,
      };

      await formService.create(normalizedStudyId, payload);

      showSuccessToast("Formulaire créé avec succès !");
      resetForm();
      onBack(true); // refresh or close drawer
    } catch (error) {
      console.error("Erreur lors de la création du formulaire :", error);
      const detail =
        error?.response?.data?.detail ||
        "Erreur inattendue lors de la création du formulaire.";
      showErrorToast(detail);
    }
  };

  // ---------------------------
  //  Reset helper
  // ---------------------------

  const resetForm = () => {
    setFormTitle("");
    setFormDescription("");
    setFields([
      {
        id: Date.now(),
        label: "",
        field_type: "",
        required: false,
        conditions: {},
      },
    ]);
  };

  // ---------------------------
  //  Return exposed methods
  // ---------------------------

  return {
    formTitle,
    setFormTitle,
    formDescription,
    setFormDescription,
    fields,
    addField,
    deleteField,
    updateField,
    renderExtraFields,
    handleSave,
  };
};
